import type { ChatMessage } from '@elara/client-core/models/ChatMessageModel';

import { isTextPart } from '@elara/domain/models/schemas/message-schemas';

const EXCERPT_RADIUS = 60;

/** A user question plus the model replies that follow it, up to the next question. */
export interface ChatTurn {
  number: number;
  question: ChatMessage;
  responses: ChatMessage[];
}

export interface SearchMatch {
  turnNumber: number;
  message: ChatMessage;
  excerpt: string;
}

export function buildChatTurns(messages: ChatMessage[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  for (const message of messages) {
    if (message.isEventMessage) continue;

    if (message.isUserMessage) {
      turns.push({ number: turns.length + 1, question: message, responses: [] });
      continue;
    }

    if (message.isModelMessage) turns.at(-1)?.responses.push(message);
  }
  return turns;
}

export function searchTurns(turns: ChatTurn[], query: string): SearchMatch[] {
  const needle = query.toLowerCase();
  return turns.flatMap((turn) =>
    [turn.question, ...turn.responses].flatMap((message) => {
      const text = messageText(message);
      const matchIndex = text.toLowerCase().indexOf(needle);
      if (matchIndex === -1) return [];

      return [{ turnNumber: turn.number, message, excerpt: excerptAround(text, matchIndex, needle.length) }];
    }),
  );
}

// Reads the text parts directly: `textContent` logs a warning for messages with no text (images, tool results).
export function messageText(message: ChatMessage): string {
  return message.content
    .filter(isTextPart)
    .map((part) => part.text)
    .join('');
}

function excerptAround(text: string, matchIndex: number, matchLength: number): string {
  const start = Math.max(0, matchIndex - EXCERPT_RADIUS);
  const end = Math.min(text.length, matchIndex + matchLength + EXCERPT_RADIUS);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < text.length ? '…' : '';
  return `${prefix}${text.slice(start, end)}${suffix}`;
}
